/**
 * Calendario de mantenimientos y estado de la garantía — lógica pura.
 *
 * Nada de esto se guarda: el calendario se deriva de la fecha de venta y del
 * plan que se copió a la garantía al vender (snapshot de la política de la
 * marca). Solo se guardan los servicios realizados y las decisiones del
 * gerente sobre servicios omitidos.
 *
 *   - Fecha de cada servicio = venta + dias (siempre desde la venta, no desde
 *     el servicio anterior).
 *   - Ventana = fecha ± tolerancia (días). El km es informativo, no decide.
 *   - Solo cuentan los servicios cuya fecha cae dentro de la vigencia (una
 *     usada de 3 meses no arrastra el servicio anual).
 *   - Omitido = la ventana cerró sin servicio. Genera alerta; la garantía
 *     solo se pierde si un gerente lo decide.
 */

const MS_DIA = 24 * 60 * 60 * 1000;

/** "YYYY-MM-DD" → Date a medianoche local (evita el corrimiento UTC). */
export function aFecha(iso) {
  if (iso instanceof Date) return new Date(iso.getFullYear(), iso.getMonth(), iso.getDate());
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Date → "YYYY-MM-DD". */
export function aIso(fecha) {
  const f = aFecha(fecha);
  const mm = String(f.getMonth() + 1).padStart(2, "0");
  const dd = String(f.getDate()).padStart(2, "0");
  return `${f.getFullYear()}-${mm}-${dd}`;
}

/** "YYYY-MM-DD" → "dd/mm/yyyy". */
export function fmtFecha(iso) {
  if (!iso) return "";
  const f = aFecha(iso);
  const dd = String(f.getDate()).padStart(2, "0");
  const mm = String(f.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${f.getFullYear()}`;
}

export function sumarDias(iso, dias) {
  const f = aFecha(iso);
  return aIso(new Date(f.getFullYear(), f.getMonth(), f.getDate() + dias));
}

/** Suma meses de calendario; el día se ajusta al último del mes si no existe. */
export function sumarMeses(iso, meses) {
  const f = aFecha(iso);
  const destino = new Date(f.getFullYear(), f.getMonth() + meses, 1);
  const ultimo = new Date(destino.getFullYear(), destino.getMonth() + 1, 0).getDate();
  destino.setDate(Math.min(f.getDate(), ultimo));
  return aIso(destino);
}

export function diasEntre(desdeIso, hastaIso) {
  return Math.round((aFecha(hastaIso) - aFecha(desdeIso)) / MS_DIA);
}

export const ESTADOS_SERVICIO = {
  proximo: { label: "Próximo", variant: "neutral" },
  letoca: { label: "Le toca", variant: "info" },
  hecho: { label: "Hecho", variant: "success" },
  tarde: { label: "Hecho tarde", variant: "warning" },
  omitido: { label: "Omitido", variant: "danger" },
};

export const ESTADOS_GARANTIA = {
  vigente: { label: "Vigente", variant: "success" },
  vencida: { label: "Vencida", variant: "neutral" },
  perdida: { label: "Perdida", variant: "danger" },
  anulada: { label: "Anulada", variant: "neutral" },
  cerrada: { label: "Revendida", variant: "neutral" },
};

export function fechaFin(garantia) {
  return sumarMeses(garantia.fechaVenta, garantia.meses);
}

/**
 * Calendario de la garantía al día `hoy`.
 * @returns {Array<{numero, nombre, dias, kmRef, tolerancia, precio, fecha,
 *   desde, hasta, estado, servicio, decision}>}
 */
export function calendario(garantia, servicios, hoy) {
  const fin = fechaFin(garantia);
  const realizados = servicios.filter((s) => s.garantiaId === garantia.id);
  const decisiones = garantia.decisiones || [];
  return (garantia.plan || [])
    .map((p) => {
      const fecha = sumarDias(garantia.fechaVenta, p.dias);
      const desde = sumarDias(fecha, -p.tolerancia);
      const hasta = sumarDias(fecha, p.tolerancia);
      const servicio = realizados.find((s) => s.numero === p.numero) || null;
      let estado;
      if (servicio) estado = aFecha(servicio.fecha) > aFecha(hasta) ? "tarde" : "hecho";
      else if (aFecha(hoy) < aFecha(desde)) estado = "proximo";
      else if (aFecha(hoy) <= aFecha(hasta)) estado = "letoca";
      else estado = "omitido";
      const decision = decisiones.find((d) => d.numero === p.numero) || null;
      return { ...p, fecha, desde, hasta, estado, servicio, decision };
    })
    .filter((s) => aFecha(s.fecha) <= aFecha(fin));
}

/** Servicios extra (fuera del plan) de la garantía. */
export function serviciosExtra(garantia, servicios) {
  return servicios.filter((s) => s.garantiaId === garantia.id && s.numero == null);
}

/**
 * anulada (la factura se anuló) y cerrada (el chasis se revendió) los
 * deriva el backend de factmot; ganan sobre todo lo demás.
 */
export function estadoGarantia(garantia, hoy) {
  if (garantia.anulada) return "anulada";
  if (garantia.cerrada) return "cerrada";
  if ((garantia.decisiones || []).some((d) => d.tipo === "perdida")) return "perdida";
  if (aFecha(hoy) > aFecha(fechaFin(garantia))) return "vencida";
  return "vigente";
}

/**
 * Omitidos que esperan decisión del gerente. Una garantía vencida o ya
 * perdida no alerta: no hay nada que decidir.
 */
export function alertasPendientes(garantia, servicios, hoy) {
  if (estadoGarantia(garantia, hoy) !== "vigente") return [];
  return calendario(garantia, servicios, hoy).filter((s) => s.estado === "omitido" && !s.decision);
}

/** Resumen para la consulta y el reporte. */
export function resumen(garantia, servicios, hoy) {
  const cal = calendario(garantia, servicios, hoy);
  const estado = estadoGarantia(garantia, hoy);
  const fin = fechaFin(garantia);
  return {
    estado,
    fin,
    diasRestantes: estado === "vigente" ? Math.max(0, diasEntre(hoy, fin)) : 0,
    hechos: cal.filter((s) => s.estado === "hecho" || s.estado === "tarde").length,
    total: cal.length,
    calendario: cal,
    alertas: estado === "vigente" ? cal.filter((s) => s.estado === "omitido" && !s.decision) : [],
  };
}

/**
 * Política aplicable a una marca: la propia si existe, si no la "por
 * defecto". Comparación sin mayúsculas ni espacios sobrantes (marca es texto
 * libre en producto/factmot).
 */
export function politicaDeMarca(politicas, marca) {
  const m = String(marca || "")
    .trim()
    .toUpperCase();
  return (
    politicas.find((p) => !p.porDefecto && String(p.marca).trim().toUpperCase() === m) ||
    politicas.find((p) => p.porDefecto) ||
    null
  );
}

/**
 * Valida el plan de una política. Devuelve el primer error o null.
 * Los días deben ser positivos y estrictamente crecientes (el número de
 * servicio sigue el orden del calendario).
 */
/**
 * Garantía limitada a piezas: meses generales derivados de las piezas (la
 * que más dura). Sin piezas devuelve null y mandan los meses generales.
 */
export function mesesDePiezas(piezas) {
  if (!piezas || !piezas.length) return null;
  return {
    mesesNueva: Math.max(...piezas.map((x) => Number(x.mesesNueva) || 0)),
    mesesUsada: Math.max(...piezas.map((x) => Number(x.mesesUsada) || 0)),
  };
}

/**
 * Cobertura por pieza de una garantía emitida: [{nombre, meses, vence,
 * vigente}]. Vacío = garantía general (sin limitar a piezas).
 */
export function coberturaPiezas(garantia, hoy) {
  return (garantia.piezas || []).map((x) => {
    const vence = sumarMeses(garantia.fechaVenta, Number(x.meses) || 0);
    return { ...x, vence, vigente: aFecha(hoy) <= aFecha(vence) };
  });
}

export function validarPolitica(p) {
  if (!p.porDefecto && !String(p.marca || "").trim()) return "Indique la marca";
  const piezas = p.piezas || [];
  const vistas = new Set();
  for (let i = 0; i < piezas.length; i += 1) {
    const x = piezas[i];
    const nombre = String(x.nombre || "").trim();
    if (!nombre) return `La pieza ${i + 1} no tiene nombre`;
    if (vistas.has(nombre.toUpperCase())) return `La pieza ${nombre} está repetida`;
    vistas.add(nombre.toUpperCase());
    if (Number(x.mesesNueva) < 0 || Number(x.mesesUsada) < 0)
      return `La pieza ${nombre} no admite meses negativos`;
  }
  if (piezas.length && !piezas.some((x) => Number(x.mesesNueva) > 0))
    return "Al menos una pieza debe tener meses para motos nuevas";
  if (!piezas.length && !(Number(p.mesesNueva) > 0))
    return "Los meses de garantía para nuevas deben ser mayores que cero";
  if (Number(p.mesesUsada) < 0) return "Los meses para usadas no pueden ser negativos";
  let anterior = 0;
  for (let i = 0; i < p.servicios.length; i += 1) {
    const s = p.servicios[i];
    const n = i + 1;
    if (!String(s.nombre || "").trim()) return `El servicio ${n} no tiene nombre`;
    if (!(Number(s.dias) > 0)) return `El servicio ${n} debe tener días mayores que cero`;
    if (Number(s.dias) <= anterior)
      return `El servicio ${n} debe ir después del anterior (días crecientes)`;
    if (Number(s.tolerancia) < 0) return `La tolerancia del servicio ${n} no puede ser negativa`;
    if (Number(s.precio) < 0) return `El precio del servicio ${n} no puede ser negativo`;
    anterior = Number(s.dias);
  }
  return null;
}

/**
 * Etiqueta corta del estado de una garantía para mostrarla fuera del módulo
 * (expediente del préstamo, compras del cliente).
 *   texto: "Vigente hasta dd/mm/yyyy" (+ aviso de servicio), "Vencida el …",
 *          "Perdida", "Anulada" o "Revendida".
 *   tono:  "ok" | "aviso" | "peligro" | "neutro".
 */
export function etiquetaGarantia(garantia, servicios, hoy) {
  const r = resumen(garantia, servicios, hoy);
  if (r.estado === "vigente") {
    let texto = `Vigente hasta ${fmtFecha(r.fin)}`;
    let tono = "ok";
    if (r.alertas.length) {
      texto += " · servicio omitido";
      tono = "aviso";
    } else if (r.calendario.some((s) => s.estado === "letoca")) {
      texto += " · le toca servicio";
    }
    return { texto, tono };
  }
  if (r.estado === "vencida") return { texto: `Vencida el ${fmtFecha(r.fin)}`, tono: "neutro" };
  if (r.estado === "perdida") return { texto: "Perdida", tono: "peligro" };
  return { texto: ESTADOS_GARANTIA[r.estado].label, tono: "neutro" };
}
